import * as i0 from '@angular/core';
import { Pipe, EventEmitter, Component, ViewEncapsulation, Host, ViewChild, Input, Output, HostBinding, NgModule } from '@angular/core';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import { smoothHeight } from '@smartdesign/angular/common';
import * as i2 from '@smartdesign/angular/link';
import { SmartDesignLinkModule } from '@smartdesign/angular/link';

class TruncateHtmlPipe {
    transform(value, length, ellipses = '...') {
        const biggestWord = 50;
        if (!value) {
            return value;
        }
        if (value.length <= length) {
            return replaceNewLineWithBreaks(value); // preserve newlines
        }
        let truncatedText = value.slice(0, length + biggestWord);
        while (truncatedText.length > length - ellipses.length) {
            const lastSpace = truncatedText.lastIndexOf(' ');
            if (lastSpace === -1) {
                break;
            }
            // do not show symbols as last character to avoid scenarios like: text;...
            truncatedText = truncatedText.slice(0, lastSpace).replace(/[!,.?;:]$/, '');
        }
        return replaceNewLineWithBreaks(truncatedText) + ellipses;
    }
}
TruncateHtmlPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: TruncateHtmlPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
TruncateHtmlPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: TruncateHtmlPipe, name: "truncateHtml" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: TruncateHtmlPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'truncateHtml',
                }]
        }] });
const replaceNewLineWithBreaks = (text) => text.trim().replace(/(?:\r\n|\r|\n)/g, '<br />');

class ReadMoreComponent {
    constructor(rendererFactory, hostRef, cd) {
        this.cd = cd;
        this.maxChars = 150;
        this.ellipsis = '...';
        this.expanded = false;
        this.labelPosition = 'bottom';
        this.moreLabel = 'Mehr anzeigen';
        this.lessLabel = 'Weniger anzeigen';
        this.expandedChange = new EventEmitter();
        this.sdReadMore = true;
        this.displayLink = true;
        this.displayedCharLength = 150;
        this.renderer = rendererFactory.createRenderer(hostRef, null);
    }
    ngAfterViewInit() {
        const viewRef = this.templRef.createEmbeddedView(null);
        const element = this.renderer.createElement('div');
        viewRef.rootNodes.forEach((node) => element.appendChild(node));
        this.html = element.innerHTML;
        this.displayedCharLength = this.expanded ? this.html.length : this.maxChars;
        this.displayLink = this.html.length > this.maxChars;
        this.cd.detectChanges();
    }
    toggle() {
        this.startHeight = this.content.nativeElement.clientHeight;
        this.expanded = !this.expanded;
        this.displayedCharLength = this.expanded ? this.html.length : this.maxChars;
        this.expandedChange.emit(this.expanded);
    }
}
ReadMoreComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: ReadMoreComponent, deps: [{ token: i0.RendererFactory2 }, { token: i0.ElementRef, host: true }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
ReadMoreComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: ReadMoreComponent, selector: "sd-read-more", inputs: { maxChars: "maxChars", ellipsis: "ellipsis", expanded: "expanded", labelPosition: "labelPosition", moreLabel: "moreLabel", lessLabel: "lessLabel" }, outputs: { expandedChange: "expandedChange" }, host: { properties: { "class.sd-read-more": "this.sdReadMore" } }, viewQueries: [{ propertyName: "templRef", first: true, predicate: ["templ"], descendants: true }, { propertyName: "content", first: true, predicate: ["content"], descendants: true }], exportAs: ["sdReadMore"], ngImport: i0, template: "<ng-container *ngIf=\"displayLink && labelPosition === 'top'; then labelTempl\"></ng-container>\n<div #content [@grow]=\"{value: expanded, params: {startHeight: startHeight}}\" [innerHTML]=\"html | truncateHtml: displayedCharLength : ellipsis\" class=\"has-font-100\">\n</div>\n<ng-template #templ><ng-content></ng-content></ng-template>\n<ng-container *ngIf=\"displayLink && labelPosition === 'bottom'; then labelTempl\"></ng-container>\n<ng-template #labelTempl>\n  <a sdLink\n     (click)=\"toggle()\"\n     [variant]=\"expanded ? 'readless' : 'readmore'\"\n     [class.mt-75]=\"labelPosition === 'bottom'\"\n     [class.mb-75]=\"labelPosition === 'top'\">\n    {{ expanded ? lessLabel : moreLabel }}\n  </a>\n</ng-template>\n  \n\n", styles: [".sd-read-more{display:block;overflow:hidden}.sd-read-more>div{overflow:hidden}\n"], directives: [{ type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i2.LinkDirective, selector: "[sdLink], sd-link", inputs: ["variant", "size", "external", "inline", "leftArrow", "noArrow", "disabled"], exportAs: ["sdLink"] }], pipes: { "truncateHtml": TruncateHtmlPipe }, animations: [smoothHeight], encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: ReadMoreComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sd-read-more', encapsulation: ViewEncapsulation.None, animations: [smoothHeight], exportAs: 'sdReadMore', template: "<ng-container *ngIf=\"displayLink && labelPosition === 'top'; then labelTempl\"></ng-container>\n<div #content [@grow]=\"{value: expanded, params: {startHeight: startHeight}}\" [innerHTML]=\"html | truncateHtml: displayedCharLength : ellipsis\" class=\"has-font-100\">\n</div>\n<ng-template #templ><ng-content></ng-content></ng-template>\n<ng-container *ngIf=\"displayLink && labelPosition === 'bottom'; then labelTempl\"></ng-container>\n<ng-template #labelTempl>\n  <a sdLink\n     (click)=\"toggle()\"\n     [variant]=\"expanded ? 'readless' : 'readmore'\"\n     [class.mt-75]=\"labelPosition === 'bottom'\"\n     [class.mb-75]=\"labelPosition === 'top'\">\n    {{ expanded ? lessLabel : moreLabel }}\n  </a>\n</ng-template>\n  \n\n", styles: [".sd-read-more{display:block;overflow:hidden}.sd-read-more>div{overflow:hidden}\n"] }]
        }], ctorParameters: function () {
        return [{ type: i0.RendererFactory2 }, { type: i0.ElementRef, decorators: [{
                        type: Host
                    }] }, { type: i0.ChangeDetectorRef }];
    }, propDecorators: { templRef: [{
                type: ViewChild,
                args: ['templ']
            }], content: [{
                type: ViewChild,
                args: ['content']
            }], maxChars: [{
                type: Input
            }], ellipsis: [{
                type: Input
            }], expanded: [{
                type: Input
            }], labelPosition: [{
                type: Input
            }], moreLabel: [{
                type: Input
            }], lessLabel: [{
                type: Input
            }], expandedChange: [{
                type: Output
            }], sdReadMore: [{
                type: HostBinding,
                args: ['class.sd-read-more']
            }] } });

class SmartDesignReadMoreModule {
}
SmartDesignReadMoreModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: SmartDesignReadMoreModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SmartDesignReadMoreModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: SmartDesignReadMoreModule, declarations: [ReadMoreComponent, TruncateHtmlPipe], imports: [CommonModule, SmartDesignLinkModule], exports: [ReadMoreComponent] });
SmartDesignReadMoreModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: SmartDesignReadMoreModule, imports: [[CommonModule, SmartDesignLinkModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: SmartDesignReadMoreModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [ReadMoreComponent, TruncateHtmlPipe],
                    imports: [CommonModule, SmartDesignLinkModule],
                    exports: [ReadMoreComponent],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { ReadMoreComponent, SmartDesignReadMoreModule, TruncateHtmlPipe };
